import { KeyRound, LogOut, Settings, Users } from '@orgus/ui/icons';
import { useNavigate } from 'react-router';
import { ROLE_DESCRIPTION } from '@orgus/protocol';
import {
  Avatar,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  toast,
} from '@orgus/ui';
import { startData } from '../../lib/data.ts';
import { useOrgus } from '../../lib/store.ts';
import { authClient } from './client.ts';
import { ROLE_LABEL, ROLE_TONE, displayName, signedInVia } from './labels.ts';
import { useMe } from './useMe.ts';

/** The signed-in person in the top bar: who, which role, how; settings and sign out. */
export function UserMenu() {
  const status = useMe((s) => s.status);
  const me = useMe((s) => s.me);
  const navigate = useNavigate();

  if (status === 'loading') return <Skeleton className="size-6 shrink-0" round />;
  if (!me) return null;
  const name = displayName(me);
  const login = me.member?.provider === 'github' ? `@${me.member.login}` : me.actor.id;

  const signOut = async () => {
    await authClient.signOut();
    useMe.getState().setSignedOut();
    if (useOrgus.getState().mode !== 'mock') startData();
    toast('Signed out', { description: 'This browser no longer has access to the hub.' });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-7 shrink-0 items-center gap-2 rounded-full border border-transparent pl-0.5 pr-1 text-sm text-fg-muted transition-colors hover:border-border hover:text-fg focus-visible:border-border"
        >
          <Avatar name={name} colorKey={me.actor.id} size={22} decorative />
          <span className="sr-only">{`Signed in as ${name}, ${ROLE_LABEL[me.actor.role]}. Open the account menu.`}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuLabel>
          <div className="flex items-center gap-3 px-1 py-1.5">
            <Avatar name={name} colorKey={me.actor.id} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-medium text-fg">{name}</p>
              <p className="truncate text-sm font-normal text-fg-subtle">{login}</p>
            </div>
            <Badge tone={ROLE_TONE[me.actor.role]}>{ROLE_LABEL[me.actor.role]}</Badge>
          </div>
          <p className="px-1 pb-1 text-sm font-normal text-fg-subtle">
            {signedInVia(me)}. {ROLE_DESCRIPTION[me.actor.role]}
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Settings />} onSelect={() => navigate('/settings')}>
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Users />} onSelect={() => navigate('/settings/members')}>
          Members & sign-in
        </DropdownMenuItem>
        <DropdownMenuItem icon={<KeyRound />} onSelect={() => navigate('/settings/tokens')}>
          API tokens
        </DropdownMenuItem>
        {me.via !== 'token' && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem icon={<LogOut />} onSelect={() => void signOut()}>
              Sign out
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
